import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Customizes Trix editor: removes attachments button, adds RTL/LTR direction buttons, and text color picker
export default class extends Controller {
  connect() {
    // Configure Trix to support color text attribute
    this.configureColorAttribute()
    
    // Listen for Trix initialization (both on element and document level)
    this.element.addEventListener("trix-initialize", this.customizeToolbar.bind(this))
    document.addEventListener("trix-initialize", this.customizeToolbar.bind(this))
    
    // Also check if toolbar already exists (in case event already fired)
    setTimeout(() => {
      const editor = this.element.querySelector("trix-editor")
      if (editor && editor.toolbarElement) {
        this.customizeToolbar({ target: editor })
      }
    }, 100)
    
    // Prevent file attachments globally
    document.addEventListener("trix-file-accept", (event) => {
      event.preventDefault()
    })
  }

  configureColorAttribute() {
    // Configure Trix to support color as a text attribute
    if (typeof Trix !== 'undefined' && Trix.config) {
      Trix.config.textAttributes.color = {
        styleProperty: "color",
        inheritable: true,
        parser: function(element) {
          return element.style.color || window.getComputedStyle(element).color
        }
      }
    }
  }

  customizeToolbar(event) {
    const editor = event.target
    
    // Only customize if this editor is within our controller's scope
    if (!this.element.contains(editor)) return

    const toolbar = editor.toolbarElement
    if (!toolbar) return
    
    // Remove attachments button group if it exists
    const fileToolsGroup = toolbar.querySelector(".trix-button-group--file-tools")
    if (fileToolsGroup) {
      fileToolsGroup.remove()
    }

    // Check if buttons already exist to avoid duplicates
    const existingRtl = toolbar.querySelector('button[data-direction="rtl"]')
    const existingLtr = toolbar.querySelector('button[data-direction="ltr"]')
    const existingColor = toolbar.querySelector('button[data-trix-attribute="color"]')
    
    if (existingRtl && existingLtr && existingColor) return

    // Find the first button group to add custom buttons
    const firstButtonGroup = toolbar.querySelector(".trix-button-group")
    if (!firstButtonGroup) return

    // Create color picker button if it doesn't exist
    if (!existingColor) {
      const colorButton = document.createElement("button")
      colorButton.type = "button"
      colorButton.className = "trix-button trix-button--text"
      colorButton.setAttribute("data-trix-attribute", "color")
      colorButton.setAttribute("title", "צבע טקסט")
      colorButton.textContent = "A"
      colorButton.style.color = "#000"
      colorButton.addEventListener("click", (e) => {
        e.preventDefault()
        this.showColorPicker(editor, colorButton)
      })
      firstButtonGroup.appendChild(colorButton)
    }

    // Create RTL button if it doesn't exist
    if (!existingRtl) {
      const rtlButton = document.createElement("button")
      rtlButton.type = "button"
      rtlButton.className = "trix-button trix-button--text"
      rtlButton.setAttribute("data-direction", "rtl")
      rtlButton.setAttribute("title", "ימין לשמאל (RTL)")
      rtlButton.textContent = "RTL"
      rtlButton.addEventListener("click", (e) => {
        e.preventDefault()
        this.setDirection(editor, "rtl")
      })
      firstButtonGroup.appendChild(rtlButton)
    }

    // Create LTR button if it doesn't exist
    if (!existingLtr) {
      const ltrButton = document.createElement("button")
      ltrButton.type = "button"
      ltrButton.className = "trix-button trix-button--text"
      ltrButton.setAttribute("data-direction", "ltr")
      ltrButton.setAttribute("title", "שמאל לימין (LTR)")
      ltrButton.textContent = "LTR"
      ltrButton.addEventListener("click", (e) => {
        e.preventDefault()
        this.setDirection(editor, "ltr")
      })
      firstButtonGroup.appendChild(ltrButton)
    }
  }

  setDirection(editor, direction) {
    const trixEditor = editor.editor
    const selectedRange = trixEditor.getSelectedRange()
    
    if (selectedRange[0] === selectedRange[1]) {
      // No selection - set direction for entire editor
      editor.setAttribute("dir", direction)
    } else {
      // Selection exists - wrap selected text in a span with dir attribute
      const document = trixEditor.getDocument()
      const selectedText = document.getTextInRange(selectedRange)
      
      // Insert formatted text with direction
      const formattedText = `<span dir="${direction}">${selectedText}</span>`
      trixEditor.insertHTML(formattedText)
    }
  }

  showColorPicker(editor, button) {
    // Get the current selection before opening the picker
    const trixEditor = editor.editor
    const selectedRange = trixEditor.getSelectedRange()
    
    // Check if there's a selection
    if (selectedRange[0] === selectedRange[1]) {
      // No selection - show a message or do nothing
      alert("אנא בחר טקסט לפני בחירת צבע")
      return
    }
    
    // Store the selection range for later use
    this.pendingColorSelection = selectedRange
    
    // Close existing color picker if open
    const existingPicker = document.querySelector('.trix-color-picker')
    if (existingPicker) {
      existingPicker.remove()
    }
    
    // Get button position to position color picker near it
    const buttonRect = button.getBoundingClientRect()
    const isRTL = document.dir === 'rtl' || 
                  document.documentElement.dir === 'rtl' ||
                  getComputedStyle(document.body).direction === 'rtl'
    
    // Predefined color palette
    const colors = [
      '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
      '#FF0000', '#FF6600', '#FFCC00', '#33FF00', '#00FFCC', '#0066FF',
      '#6600FF', '#CC00FF', '#FF0099', '#FF3366', '#FF9933', '#FFFF00',
      '#99FF00', '#00FF99', '#0099FF', '#6633FF', '#CC33FF', '#FF3399'
    ]
    
    // Create color picker dropdown
    const colorPicker = document.createElement("div")
    colorPicker.className = "trix-color-picker"
    colorPicker.style.position = "fixed"
    
    // Calculate position - ensure it fits in viewport
    const pickerWidth = 180
    const pickerHeight = 200 // Approximate height
    let leftPos = buttonRect.left
    let topPos = buttonRect.bottom + 5
    
    // Adjust horizontal position to fit in viewport
    if (isRTL) {
      // In RTL, align to right edge of button
      leftPos = buttonRect.right - pickerWidth
      if (leftPos < 10) {
        leftPos = 10 // Keep some margin from left edge
      }
    } else {
      // In LTR, align to left edge of button
      if (leftPos + pickerWidth > window.innerWidth - 10) {
        leftPos = window.innerWidth - pickerWidth - 10 // Keep some margin from right edge
      }
    }
    
    // Adjust vertical position if needed
    if (topPos + pickerHeight > window.innerHeight - 10) {
      // Not enough space below, position above button
      topPos = buttonRect.top - pickerHeight - 5
      if (topPos < 10) {
        topPos = 10 // Keep some margin from top edge
      }
    }
    
    colorPicker.style.left = `${leftPos}px`
    colorPicker.style.top = `${topPos}px`
    colorPicker.style.zIndex = "10000"
    colorPicker.style.backgroundColor = "#fff"
    colorPicker.style.border = "1px solid #ccc"
    colorPicker.style.borderRadius = "4px"
    colorPicker.style.padding = "8px"
    colorPicker.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"
    colorPicker.style.display = "grid"
    colorPicker.style.gridTemplateColumns = "repeat(6, 1fr)"
    colorPicker.style.gap = "4px"
    colorPicker.style.width = "180px"
    
    // Create color swatches
    colors.forEach(color => {
      const swatch = document.createElement("div")
      swatch.style.width = "24px"
      swatch.style.height = "24px"
      swatch.style.backgroundColor = color
      swatch.style.border = color === '#FFFFFF' ? "1px solid #ccc" : "1px solid transparent"
      swatch.style.borderRadius = "3px"
      swatch.style.cursor = "pointer"
      swatch.style.transition = "transform 0.1s"
      
      swatch.addEventListener("mouseenter", () => {
        swatch.style.transform = "scale(1.2)"
        swatch.style.zIndex = "1"
      })
      
      swatch.addEventListener("mouseleave", () => {
        swatch.style.transform = "scale(1)"
      })
      
      swatch.addEventListener("click", () => {
        this.applyColor(editor, color, button)
        this.closeColorPicker()
      })
      
      colorPicker.appendChild(swatch)
    })
    
    // Add custom color input at the bottom
    const customColorContainer = document.createElement("div")
    customColorContainer.style.gridColumn = "1 / -1"
    customColorContainer.style.marginTop = "8px"
    customColorContainer.style.paddingTop = "8px"
    customColorContainer.style.borderTop = "1px solid #eee"
    customColorContainer.style.display = "flex"
    customColorContainer.style.alignItems = "center"
    customColorContainer.style.gap = "8px"
    
    const customColorLabel = document.createElement("span")
    customColorLabel.textContent = "צבע מותאם:"
    customColorLabel.style.fontSize = "12px"
    customColorLabel.style.color = "#666"
    
    const customColorInput = document.createElement("input")
    customColorInput.type = "color"
    customColorInput.value = "#000000"
    customColorInput.style.width = "40px"
    customColorInput.style.height = "30px"
    customColorInput.style.border = "1px solid #ccc"
    customColorInput.style.borderRadius = "3px"
    customColorInput.style.cursor = "pointer"
    
    customColorInput.addEventListener("change", (e) => {
      this.applyColor(editor, e.target.value, button)
      this.closeColorPicker()
    })
    
    customColorContainer.appendChild(customColorLabel)
    customColorContainer.appendChild(customColorInput)
    colorPicker.appendChild(customColorContainer)
    
    document.body.appendChild(colorPicker)
    
    // Store reference for cleanup
    this.colorPickerElement = colorPicker
    
    // Close on click outside
    setTimeout(() => {
      const clickHandler = (e) => {
        if (!colorPicker.contains(e.target) && e.target !== button) {
          this.closeColorPicker()
          document.removeEventListener("click", clickHandler)
        }
      }
      document.addEventListener("click", clickHandler)
    }, 10)
    
    // Close on escape key
    const escapeHandler = (e) => {
      if (e.key === "Escape") {
        this.closeColorPicker()
        document.removeEventListener("keydown", escapeHandler)
      }
    }
    document.addEventListener("keydown", escapeHandler)
  }
  
  closeColorPicker() {
    if (this.colorPickerElement && document.body.contains(this.colorPickerElement)) {
      this.colorPickerElement.remove()
    }
    this.colorPickerElement = null
    this.pendingColorSelection = null
  }

  applyColor(editor, color, button) {
    const trixEditor = editor.editor
    
    // Use stored selection if available, otherwise get current selection
    const selectedRange = this.pendingColorSelection || trixEditor.getSelectedRange()
    
    if (selectedRange[0] === selectedRange[1]) {
      // No selection - do nothing
      return
    }
    
    // Ensure selection is set
    trixEditor.setSelectedRange(selectedRange)
    
    // Get selected text from the DOM (more reliable than Trix document API)
    const selection = window.getSelection()
    let selectedText = ""
    
    if (selection.rangeCount > 0) {
      selectedText = selection.toString()
    } else {
      // Fallback: get text from Trix document using the range
      const trixDocument = trixEditor.getDocument()
      // Extract text manually from the document
      const length = selectedRange[1] - selectedRange[0]
      // Get the document's text content and extract the range
      const fullText = trixDocument.toString()
      selectedText = fullText.substring(selectedRange[0], selectedRange[1])
    }
    
    if (!selectedText || selectedText.trim() === "") {
      return
    }
    
    // Try using Trix's attribute system first (if configured)
    if (Trix.config && Trix.config.textAttributes && Trix.config.textAttributes.color) {
      try {
        // Use Trix's activateAttribute if available
        trixEditor.activateAttribute("color", color)
        button.style.color = color
        this.pendingColorSelection = null
        return
      } catch (e) {
        logger.log("Trix attribute system not working, falling back to HTML insertion", e)
      }
    }
    
    // Fallback: Insert HTML with color style
    // Restore selection first
    trixEditor.setSelectedRange(selectedRange)
    
    // Insert HTML with color - insertHTML replaces the current selection automatically
    const formattedText = `<span style="color: ${color}">${selectedText}</span>`
    trixEditor.insertHTML(formattedText)
    
    // Trigger input event to ensure Trix updates
    editor.dispatchEvent(new Event("input", { bubbles: true }))
    
    // Update button color to show current selection
    button.style.color = color
    
    // Clear pending selection
    this.pendingColorSelection = null
  }
}

